/**
 * What the tracker shows (initiative brief 25.10): words of the server's notices, «ты после: …», who is «mine».
 * Pure helpers — the order and the rules are the server's (shared/initiative.ts).
 */
import { toast } from 'sonner';
import type { InitiativeEntry, InitiativeNotice, InitiativeUndo } from '@roll1/shared';
import type { TranslationKeys } from '../i18n/types';

type T = (key: keyof TranslationKeys) => string;

export function fmt(s: string, params: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m));
}

export const signed = (n: number): string => (n < 0 ? `−${Math.abs(n)}` : `+${n}`);
/** «15 + 2 = 17» */
export const rollSum = (d20: number, mod: number): string => `${d20} ${mod < 0 ? '−' : '+'} ${Math.abs(mod)} = ${d20 + mod}`;

function noticeText(n: InitiativeNotice, t: T): string {
  switch (n.kind) {
    case 'started': return n.pending.length ? t('initiative.n.started') : t('initiative.n.startedAll');
    case 'begun': return n.pending.length ? fmt(t('initiative.n.begun'), { names: n.pending.join(', ') }) : fmt(t('initiative.n.round'), { n: 1 });
    case 'added': return n.items.map((i) => {
      const roll = i.d20 !== undefined && i.mod !== undefined ? rollSum(i.d20, i.mod) : String(i.value ?? '');
      if (i.group) return fmt(t('initiative.n.addedGroup'), { name: i.name, group: i.group, v: i.value ?? '' });
      if (i.fromRound) return fmt(t('initiative.n.addedLater'), { name: i.name, roll, n: i.fromRound });
      return fmt(t('initiative.n.addedNow'), { name: i.name, roll });
    }).join(' · ');
    case 'removed': return fmt(t(n.names.length > 1 ? 'initiative.n.removedMany' : 'initiative.n.removedOne'), { names: n.names.join(', ') });
    case 'renumbered': {
      const roll = n.d20 !== undefined && n.mod !== undefined ? rollSum(n.d20, n.mod) : String(n.value ?? '');
      const verb = n.how === 'value' ? fmt(t('initiative.n.value'), { v: n.value ?? '' })
        : n.how === 'roll' ? fmt(t('initiative.n.roll'), { roll })
        : n.how === 'reroll' ? fmt(t('initiative.n.reroll'), { roll })
        : fmt(t('initiative.n.move'), { v: n.value ?? '' });
      const cur = n.current ?? '';
      const tail = n.outcome === 'top' ? fmt(t('initiative.n.top'), { cur })
        : n.outcome === 'keeps' ? t('initiative.n.keeps')
        : n.outcome === 'owed' ? fmt(t('initiative.n.owed'), { r: n.round, r1: n.round + 1 })
        : n.outcome === 'acted' ? fmt(t('initiative.n.acted'), { r: n.round, r1: n.round + 1 })
        : n.outcome === 'allRolled' ? fmt(t('initiative.n.allRolled'), { cur })
        : n.outcome === 'same' ? t('initiative.n.same') : '';
      return `${n.name}: ${verb}${tail ? `. ${tail}` : ''}`;
    }
    case 'round': {
      const head = fmt(t('initiative.n.round'), { n: n.round });
      if (!n.lost.length) return head;
      return `${head} · ${fmt(t('initiative.n.lost'), { names: n.lost.map((l) => l.name).join(', '), r: n.round - 1, v: n.lost[0].value ?? '' })}`;
    }
    case 'delayed': return fmt(t('initiative.n.delayed'), { name: n.name, r: n.round, r1: n.round + 1 });
    case 'joined': return fmt(t('initiative.n.joined'), { name: n.name, before: n.before ?? '', v: n.value ?? '' });
    case 'split': return fmt(t('initiative.n.split'), { rolls: n.items.map((i) => `${i.name} ${rollSum(i.d20, i.mod)}`).join(' · ') }) + (n.owed ? `. ${t('initiative.n.splitOwed')}` : '');
    case 'ended': return fmt(t('initiative.n.ended'), { n: n.rounds });
    case 'restored': return t('initiative.n.restored');
    case 'firstTurn': return t('initiative.n.firstTurn');
    case 'back': return fmt(t('initiative.n.back'), { cur: n.current ?? '' });
    case 'turnEnded': return fmt(t('initiative.n.turnEnded'), { name: n.name });
  }
}

/** One toast for a change; «Отменить» when the change itself is undoable (added / removed / ended). */
export function showInitiativeNotices(notices: InitiativeNotice[], undo: InitiativeUndo | undefined, sendUndo: (id: string) => void, t: T): void {
  const text = notices.map((n) => noticeText(n, t)).filter(Boolean).join(' · ');
  if (!text) return;
  const undoable = undo && notices.some((n) => n.kind === 'added' || n.kind === 'removed' || n.kind === 'ended');
  toast(text, {
    id: 'initiative-notice',
    duration: undoable ? 8000 : 5000,
    ...(undoable ? { action: { label: t('initiative.undo'), onClick: () => sendUndo(undo!.id) } } : {}),
  });
}

/** Members of an entry (a single participant is a group of one). */
export function entryMembers(e: InitiativeEntry): Array<{ tokenId: string; name: string }> {
  return e.members && e.members.length > 0 ? e.members : [{ tokenId: e.tokenId, name: e.name }];
}

/**
 * Р4 for a player: «ты следующий» / «ты после: Элара» — by the visible list only (a count of turns would give away the
 * hidden ones). Nothing while a hidden one acts (currentIndex -1) or before round 1.
 */
export function untilMe(entries: InitiativeEntry[], currentIndex: number, isMine: (e: InitiativeEntry) => boolean): { kind: 'next' | 'after' | 'delayed' | 'none'; name?: string } {
  const me = entries.find(isMine);
  if (!me) return { kind: 'none' };
  if (me.delayedRound !== undefined) return { kind: 'delayed' };
  if (currentIndex < 0) return { kind: 'none' };
  const cur = entries[currentIndex];
  if (!cur || cur.id === me.id) return { kind: 'none' };
  const list = entries.filter((e) => e.delayedRound === undefined && e.value !== null);
  const mi = list.indexOf(me);
  if (mi < 0) return { kind: 'none' };
  const before = list[(mi - 1 + list.length) % list.length];
  if (!before) return { kind: 'none' };
  if (before.id === cur.id) return { kind: 'next' };
  return { kind: 'after', name: before.name };
}
